import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, RotateCcw, AlertTriangle, Clock, X, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import paymentsService, {
  detectProvider, type PaymentStatus, type PaymentProvider, type RefundableInfo, type RefundRecord,
} from '@/services/payments.service';
import { formatTime } from '@/lib/format';
import { useTranslation } from 'react-i18next';

const STATUS_META: Record<string, { label: string; cls: string }> = {
  paid: { label: 'Bezahlt', cls: 'bg-emerald-50 text-emerald-700' },
  pending: { label: 'Ausstehend', cls: 'bg-amber-50 text-amber-700' },
  failed: { label: 'Fehlgeschlagen', cls: 'bg-rose-50 text-rose-700' },
  refunded: { label: 'Erstattet', cls: 'bg-slate-100 text-slate-600' },
  partially_refunded: { label: 'Teilw. erstattet', cls: 'bg-violet-50 text-violet-700' },
  unknown: { label: 'Unbekannt', cls: 'bg-slate-100 text-slate-500' },
};
const PROVIDER_LABEL: Record<string, string> = { paypal: 'PayPal', stripe: 'Stripe', klarna: 'Klarna', shopify: 'Shopify', woocommerce: 'WooCommerce' };
const PROVIDER_COLOR: Record<string, string> = { paypal: '#003087', stripe: '#635BFF', klarna: '#D6699A', shopify: '#5E8E3E', woocommerce: '#7F54B3' };
const REFUND_STATUS: Record<RefundRecord['status'], { label: string; cls: string }> = {
  scheduled: { label: 'geplant', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  processing: { label: 'läuft', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  done: { label: 'erstattet', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  failed: { label: 'fehlgeschlagen', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  cancelled: { label: 'storniert', cls: 'bg-slate-50 text-slate-500 border-slate-200' },
};

const fmtTime = (iso?: string | null) => {
  if (!iso) return '';
  try { return formatTime(iso); } catch { return ''; }
};
const money = (n: number, cur: string) => `${n.toFixed(2)} ${cur}`;
const toNum = (v: string | number | undefined | null) => {
  const n = parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

/** Optionale Positionen aus der Bestellanzeige (für Anbieter ohne Positions-API, z. B. JTL/PayPal). */
/** Unübersehbarer Hinweis: Bei Vorkasse überweist das System NICHT selbst zurück. */
export const ManualTransferNotice: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <div role="alert" className={`flex w-full items-start gap-2 rounded-md border-2 border-red-500 bg-red-50 ${compact ? 'px-2 py-1.5' : 'px-3 py-2.5'} text-red-800`}>
    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
    <div className="min-w-0">
      <div className="text-[12px] font-extrabold uppercase tracking-wide">Achtung Vorkasse – manuelle Überweisung notwendig</div>
      {!compact && (
        <div className="mt-0.5 text-[11px] leading-snug">
          Der Kunde hat per Überweisung bezahlt. Eine Erstattung hier bucht sie nur im Shop – das Geld muss manuell per Banküberweisung zurückgezahlt werden.
        </div>
      )}
    </div>
  </div>
);

export interface DisplayItem { name: string; quantity: number; price: string | number }

const PaymentOrderStatus: React.FC<{
  transactionId: string; paymentMethod?: string; total?: string; currency?: string; orderNumber?: string;
  provider?: PaymentProvider; organizationId?: string; ticketId?: string; items?: DisplayItem[];
}> = ({ transactionId, paymentMethod, total, orderNumber, provider: explicitProvider, organizationId, ticketId, items }) => {
  const { t } = useTranslation();
  const provider = explicitProvider ?? detectProvider(paymentMethod);
  const [st, setSt] = useState<PaymentStatus | null>(null);
  const [loading, setLoading] = useState(false);
  // 0 zu, 1 Umfang wählen, 2 Warnung, 3 endgültige Bestätigung
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0);
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);

  // Umfang
  const [info, setInfo] = useState<RefundableInfo | null>(null);
  const [infoLoading, setInfoLoading] = useState(false);
  const [mode, setMode] = useState<'full' | 'partial'>('full');
  const [qty, setQty] = useState<Record<string, number>>({});
  const [shipping, setShipping] = useState(false);
  const [restock, setRestock] = useState(false);
  const [amountOverride, setAmountOverride] = useState<string>('');
  const [note, setNote] = useState('');

  const load = () => {
    if (!provider) return;
    setLoading(true);
    paymentsService.getStatus(provider, transactionId, organizationId).then(setSt).catch(() => setSt({ found: false, error: 'Fehler' })).finally(() => setLoading(false));
  };
  useEffect(() => { if (provider && transactionId) load(); }, [provider, transactionId]);

  const openDialog = () => {
    setStep(1); setUnderstood(false); setMode('full'); setQty({}); setShipping(false); setRestock(false); setAmountOverride(''); setNote('');
    if (!provider) return;
    setInfoLoading(true);
    paymentsService.getRefundable(provider, transactionId, organizationId)
      .then((d) => setInfo(d))
      .catch((e) => { setInfo(null); toast.error(e?.response?.data?.message || 'Erstattbare Positionen konnten nicht geladen werden'); })
      .finally(() => setInfoLoading(false));
  };

  const currency = info?.currency || st?.amount?.currency_code || 'EUR';
  const maxRefundable = info?.maxRefundable ?? toNum(st?.amount?.value);
  const lineSource = info?.supportsLineItems
    ? info.lineItems.map((l) => ({ id: l.id, name: l.name, max: l.refundableQuantity, unit: l.unitPrice, sub: l.refundedQuantity ? `${l.refundedQuantity} bereits erstattet` : (l.sku || '') }))
    : (items || []).map((it, i) => ({ id: `display-${i}`, name: it.name, max: it.quantity || 1, unit: toNum(it.price), sub: '' }));

  const computed = useMemo(() => {
    const lines = lineSource.reduce((s, l) => s + (qty[l.id] || 0) * l.unit, 0);
    const ship = shipping && info ? info.shippingRefundable : 0;
    return Math.round((lines + ship) * 100) / 100;
  }, [qty, shipping, info, lineSource]);

  const partialAmount = amountOverride.trim() ? toNum(amountOverride) : computed;
  const refundAmount = mode === 'full' ? maxRefundable : partialAmount;
  const partialValid = mode === 'full' || (partialAmount > 0 && partialAmount <= maxRefundable + 0.001);
  const amountLabel = refundAmount > 0 ? money(refundAmount, currency) : (st?.amount ? `${st.amount.value} ${st.amount.currency_code}` : total || '');

  const scheduleRefund = async () => {
    if (!provider) return;
    setBusy(true);
    try {
      const base = { orderNumber, organizationId, ticketId, note: note.trim() || undefined };
      const opts = mode === 'full' ? base : {
        ...base,
        lineItems: info?.supportsLineItems
          ? lineSource.filter((l) => (qty[l.id] || 0) > 0).map((l) => ({ id: l.id, quantity: qty[l.id], name: l.name }))
          : undefined,
        shipping: info?.supportsLineItems && shipping ? true : undefined,
        restock: info?.supportsRestock ? restock : undefined,
        // Eigener Betrag immer mitschicken, wenn es keine Positions-API gibt oder er angepasst wurde
        amount: (!info?.supportsLineItems || amountOverride.trim()) ? { value: partialAmount.toFixed(2), currency_code: currency } : undefined,
      };
      const r = await paymentsService.refund(provider, transactionId, opts);
      if (r.success) { toast.success(t('common:x2.refundPlannedIn1h', 'Rückerstattung geplant — wird in ~1 Std. ausgeführt (stornierbar)')); load(); }
      else toast.error(`Fehlgeschlagen: ${r.message || ''}`);
    } catch (e: any) { toast.error(e?.response?.data?.message || t('agent:caseStatus.failed', 'Fehlgeschlagen')); }
    finally { setBusy(false); setStep(0); setUnderstood(false); }
  };

  const cancelRefund = async (id: string) => {
    setBusy(true);
    try {
      const r = await paymentsService.cancelRefund(id);
      if (r.success) { toast.success(t('common:x2.refundCancelled', 'Rückerstattung storniert')); load(); }
      else toast.error(r.message || 'Nicht stornierbar');
    } catch { toast.error(t('agent:caseStatus.failed', 'Fehlgeschlagen')); }
    finally { setBusy(false); }
  };

  if (!provider) return null;

  const meta = st?.status ? STATUS_META[st.status] : null;
  const canRefund = st?.status === 'paid' || st?.status === 'partially_refunded';
  const amt = st?.amount ? `${st.amount.value} ${st.amount.currency_code}` : total;
  const history = (st?.refunds || []).filter((r) => r.status !== 'cancelled').slice(0, 5);
  const describe = (r: RefundRecord) => {
    const parts: string[] = [];
    if (r.amount_value) parts.push(`${r.amount_value} ${r.amount_currency || ''}`.trim());
    else if (!r.line_items?.length && !r.shipping_amount) parts.push('voll');
    if (r.line_items?.length) parts.push(r.line_items.map((l) => `${l.quantity}× ${l.name || 'Position'}`).join(', '));
    if (r.shipping_amount) parts.push('inkl. Versand');
    return parts.join(' · ');
  };

  return (
    <div className="ticket-order-payment-status flex-wrap">
      <span className="text-[11px] font-semibold" style={{ color: PROVIDER_COLOR[provider] }}>{PROVIDER_LABEL[provider]}:</span>
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
      ) : st?.found && meta ? (
        <>
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${meta.cls}`}>{meta.label}</span>
          {amt && <span className="text-[11px] text-slate-500">{amt}</span>}
          {canRefund && (
            <button onClick={openDialog} className="ticket-order-refund-action">
              <RotateCcw className="h-3 w-3" /> {t('settings:p2.refund', 'Rückerstattung')}
            </button>
          )}
        </>
      ) : (
        <span className="text-[11px] text-slate-400">{st?.error || 'nicht gefunden'}</span>
      )}

      {/* Verlauf: geplant (stornierbar), erledigt, fehlgeschlagen mit Grund */}
      {history.length > 0 && (
        <div className="mt-1 flex w-full flex-col gap-1">
          {history.map((r) => {
            const m = REFUND_STATUS[r.status];
            return (
              <div key={r.id} className={`flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-md border px-2 py-1 text-[11px] ${m.cls}`} title={r.error || undefined}>
                {r.status === 'scheduled' || r.status === 'processing' ? <Clock className="h-3 w-3" />
                  : r.status === 'done' ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                <span className="whitespace-nowrap font-semibold">Erstattung {m.label}</span>
                <span className="min-w-0 break-words">{describe(r)}</span>
                <span className="ml-auto whitespace-nowrap text-[10px] opacity-80">
                  {r.status === 'scheduled' ? `· ${fmtTime(r.execute_at)}` : fmtTime(r.executed_at || r.created_at)}
                </span>
                {r.status === 'scheduled' && (
                  <button onClick={() => cancelRefund(r.id)} disabled={busy} className="ml-1 inline-flex items-center gap-0.5 text-rose-600 hover:underline">
                    <X className="h-3 w-3" /> {t('common:action.cancel', 'Abbrechen')}
                  </button>
                )}
              </div>
            );
          })}
          {history.some((r) => r.status === 'failed') && (
            <div className="text-[10px] text-rose-600">
              {history.find((r) => r.status === 'failed')?.error}
            </div>
          )}
        </div>
      )}

      {st?.manualPayment && <div className="mt-1 w-full"><ManualTransferNotice /></div>}

      <AlertDialog open={step > 0} onOpenChange={(o) => { if (!o) { setStep(0); setUnderstood(false); } }}>
        {/* grid-cols-[minmax(0,1fr)]: lange Produktnamen (truncate) dürfen die Karte nicht sprengen */}
        <AlertDialogContent className="max-w-2xl grid-cols-[minmax(0,1fr)] overflow-hidden">

          {step === 1 ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Rückerstattung{orderNumber ? ` – Bestellung ${orderNumber}` : ''}</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-600">
                  {infoLoading ? 'Erstattbare Beträge werden geladen …'
                    : info ? <>Noch erstattbar: <strong>{money(info.maxRefundable, currency)}</strong>{info.alreadyRefunded ? <> (bereits erstattet: {money(info.alreadyRefunded, currency)})</> : null}</>
                    : 'Erstattbarer Betrag unbekannt.'}
                </AlertDialogDescription>
              </AlertDialogHeader>

              {(info?.manualPayment || st?.manualPayment) && <ManualTransferNotice />}

              <div className="flex gap-2">
                {(['full', 'partial'] as const).map((m) => (
                  <button key={m} type="button" onClick={() => setMode(m)}
                    className={`flex-1 rounded-md border px-3 py-2 text-sm ${mode === m ? 'border-rose-400 bg-rose-50 font-semibold text-rose-700' : 'hover:bg-slate-50'}`}>
                    {m === 'full' ? 'Vollständig' : 'Teilweise'}
                  </button>
                ))}
              </div>

              {mode === 'partial' && (
                <div className="space-y-3">
                  {lineSource.length > 0 && (
                    <div className="max-h-56 overflow-y-auto rounded-md border">
                      {lineSource.map((l) => (
                        <div key={l.id} className="flex items-center gap-3 border-b px-3 py-2 text-sm last:border-b-0">
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{l.name}</div>
                            <div className="text-[11px] text-slate-500">{money(l.unit, currency)} / Stk.{l.sub ? ` · ${l.sub}` : ''}</div>
                          </div>
                          <input type="number" min={0} max={l.max} value={qty[l.id] || 0} disabled={l.max <= 0}
                            onChange={(e) => setQty((q) => ({ ...q, [l.id]: Math.max(0, Math.min(l.max, parseInt(e.target.value || '0', 10) || 0)) }))}
                            className="w-16 rounded-md border px-2 py-1 text-right" aria-label={`Menge ${l.name}`} />
                          <span className="w-10 text-[11px] text-slate-500">/ {l.max}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {info?.supportsLineItems && info.shippingRefundable > 0 && (
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={shipping} onChange={(e) => setShipping(e.target.checked)} className="h-4 w-4" />
                      Versandkosten erstatten ({money(info.shippingRefundable, currency)})
                    </label>
                  )}
                  {info?.supportsRestock && (
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={restock} onChange={(e) => setRestock(e.target.checked)} className="h-4 w-4" />
                      Ausgewählte Artikel wieder auf Lager buchen
                    </label>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="whitespace-nowrap">Betrag</span>
                    <input type="text" inputMode="decimal" value={amountOverride} placeholder={computed.toFixed(2)}
                      onChange={(e) => setAmountOverride(e.target.value)} className="w-28 rounded-md border px-2 py-1 text-right" aria-label="Erstattungsbetrag" />
                    <span>{currency}</span>
                    <span className="text-[11px] text-slate-500">{amountOverride.trim() ? 'eigener Betrag' : 'aus Auswahl berechnet'}</span>
                  </div>
                  {!partialValid && (
                    <p className="text-xs text-rose-600">Bitte einen Betrag zwischen 0,01 und {money(maxRefundable, currency)} wählen.</p>
                  )}
                </div>
              )}

              <input type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={250}
                placeholder="Grund / Notiz (optional, intern beim Shop/Anbieter)" className="w-full rounded-md border px-3 py-2 text-sm" />

              <AlertDialogFooter>
                <AlertDialogCancel>{t('common:action.cancel', 'Abbrechen')}</AlertDialogCancel>
                <AlertDialogAction onClick={(e) => { e.preventDefault(); setStep(2); }} disabled={infoLoading || !partialValid || refundAmount <= 0} className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                  {t('common:action.next', 'Weiter')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : step === 2 ? (
            <>
              <AlertDialogHeader>
                <div className="mb-1 flex items-center gap-2 text-rose-600">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-100"><AlertTriangle className="h-5 w-5" /></span>
                  <AlertDialogTitle className="text-rose-700">{t('common:x2.realRefund', 'Echte Rückerstattung')}</AlertDialogTitle>
                </div>
                <AlertDialogDescription className="text-slate-600">
                  {(info?.manualPayment || st?.manualPayment) ? (<><strong className="text-red-700">Vorkasse:</strong> Die Erstattung über <strong>{amountLabel}</strong> wird nur im Shop gebucht. <strong>Das Geld müssen Sie selbst per Banküberweisung an den Kunden zurückzahlen.</strong></>) : (<>
                  <strong className="text-rose-600">{t('common:x2.attention', 'Achtung:')}</strong> Es wird <strong>echtes Geld</strong> (<strong>{amountLabel}</strong>{mode === 'partial' ? ', Teilerstattung' : ''}) über {PROVIDER_LABEL[provider]} an den Kunden zurückgezahlt{orderNumber ? <> — Bestellung <strong>{orderNumber}</strong></> : ''}. Dieser Vorgang ist <strong>nicht umkehrbar</strong>.
                  </>)}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <button onClick={() => setStep(1)} className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">{t('common:action.back', 'Zurück')}</button>
                <AlertDialogAction onClick={(e) => { e.preventDefault(); setStep(3); }} className="bg-rose-600 hover:bg-rose-700">{t('common:action.next', 'Weiter')}</AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('common:x2.confirmFinally', 'Endgültig bestätigen')}</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-600">
                  {t('common:x2.refundWillBe', 'Die Rückerstattung wird')} <strong>erst in ca. 1 Stunde automatisch ausgeführt</strong> und kann bis dahin storniert werden.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <label className="my-2 flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} className="mt-0.5 h-4 w-4 accent-rose-600" />
                <span>Ich verstehe, dass <strong>echtes Geld</strong> ({amountLabel}) an den Kunden zurückgezahlt wird.</span>
              </label>
              <AlertDialogFooter>
                <button onClick={() => setStep(2)} className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">{t('common:action.back', 'Zurück')}</button>
                <AlertDialogAction onClick={(e) => { e.preventDefault(); scheduleRefund(); }} disabled={!understood || busy} className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Rückerstattung planen'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PaymentOrderStatus;
